import { axiosInstance } from "@/lib/axios-instance";
import { getLookbookToken } from "@/lib/lookbook-access";
import type {
  LookbookDealSharing,
  LookbookSharedFile,
} from "@/api/raise-board-api";

export type LookbookSection = "profile" | "data-room" | "reports";

export interface ResolvedLookbookVisibility {
  profile: boolean;
  dataRoom: boolean;
  reports: boolean;
  files: LookbookSharedFile[];
  reportKeys: string[];
  /** The sections to render, in reading order. Never empty. */
  sections: LookbookSection[];
}

/**
 * What a public deal page should show.
 *
 * The `undefined` case is the important one: it is every /rb/:slug deal, every
 * /global deal, and every lookbook entry created before this feature existed.
 * All of them resolve to profile-only, which is exactly what those pages have
 * always rendered. This one default is why adding lookbook sharing changes
 * nothing anywhere else.
 */
export function resolveLookbookVisibility(
  sharing?: LookbookDealSharing
): ResolvedLookbookVisibility {
  const profile = sharing ? sharing.profile !== false : true;
  const dataRoom = sharing?.data_room === true;
  const reports = sharing?.reports === true;

  const files = dataRoom ? sharing?.documents ?? [] : [];
  const reportKeys = reports ? sharing?.report_keys ?? [] : [];

  const sections: LookbookSection[] = [];
  if (profile) sections.push("profile");
  // A section with nothing in it is not a section — an empty "Data Room"
  // heading reads as a broken page rather than a deliberate choice.
  if (dataRoom && files.length > 0) sections.push("data-room");
  if (reports && reportKeys.length > 0) sections.push("reports");

  // The identity card always renders, so a deal configured to show nothing at
  // all still needs somewhere to land.
  if (sections.length === 0) sections.push("profile");

  return { profile, dataRoom, reports, files, reportKeys, sections };
}

export const SECTION_ANCHORS: Record<LookbookSection, string> = {
  profile: "lb-profile",
  "data-room": "lb-data-room",
  reports: "lb-reports",
};

export const SECTION_LABELS: Record<LookbookSection, string> = {
  profile: "Profile",
  "data-room": "Data Room",
  reports: "Reports",
};

/**
 * The gated proxy URL for a shared document.
 *
 * No longer the first choice: the proxy does not serve bytes in production, so
 * the payload now carries each shared document's storage url and the data room
 * links straight at it. This stays as the fallback for a payload served by a
 * backend that predates that change, and as the thing to switch back to once
 * the proxy works — see lookbookDocumentUrl.
 */
export function lookbookFileUrl(
  publicId: string,
  companyId: string,
  fileId: string,
  opts: { download?: boolean } = {}
): string {
  const base = String(axiosInstance.defaults.baseURL ?? "").replace(/\/$/, "");
  const path = `/lookbook_file/${encodeURIComponent(
    publicId
  )}/${encodeURIComponent(companyId)}/${encodeURIComponent(fileId)}`;
  // The proxy is loaded as an iframe or link, which cannot carry the access
  // header, so a protected lookbook's token rides in the query instead.
  const query = new URLSearchParams();
  if (opts.download) query.set("download", "1");
  const token = getLookbookToken(publicId);
  if (token) query.set("lookbook_token", token);
  const qs = query.toString();
  return `${base}${path}${qs ? `?${qs}` : ""}`;
}

/**
 * Where a shared document's bytes actually are.
 *
 * The storage url when the payload carries one, the proxy url otherwise. Same
 * url for viewing and for downloading: a cross-origin storage url cannot be
 * given a Content-Disposition, so what separates the two buttons is the
 * anchor's `download` attribute, not the address.
 *
 * `opts.download` is still threaded through to the proxy fallback, where it
 * does set the disposition.
 */
export function lookbookDocumentUrl(
  file: LookbookSharedFile,
  publicId: string,
  companyId: string,
  opts: { download?: boolean } = {}
): string {
  return (
    file.file_url ?? lookbookFileUrl(publicId, companyId, file._id, opts)
  );
}
